import { createRng, hashSeed } from '../rng.js';

/**
 * Quick refresh, as a simulation.
 *
 * Things come off the pass and you put them on a tray. What makes a loaded tray
 * hard is not how much is on it, it is that it is held from underneath at one
 * point: where a cup lands matters as much as whether you caught it, and a tray
 * goes over long before it runs out of room. So the losing condition is the
 * tilt and not the count, and the stacking is Tetris only in the sense that
 * what you already put down decides where the next one can go.
 *
 * Three things move the tilt, and they are the three things a server actually
 * has to manage:
 *
 *   - **where it lands.** A plate caught on the rim is worth far more torque
 *     than the same plate caught in the middle, scaled by its mass.
 *   - **how fast you move.** Sway. Lunging across the lane to save one cup is
 *     how you lose the eleven already on there, and the sway term is what makes
 *     that true rather than making it advice.
 *   - **time.** Tilt bleeds back toward level, because between catches you are
 *     doing nothing but steadying it. Without that the round is a one-way
 *     ratchet and the only strategy is to stop catching.
 *
 * Deterministic from the level's seed, so a failed attempt is a lesson about
 * that round rather than about that day's luck.
 */

/** The lane, in tray widths either side of the middle. */
export const LANE = Object.freeze({ width: 12, top: 9 });

/** The tray. Half-width is what every offset is measured against. */
export const TRAY = Object.freeze({ width: 2.6, y: 0.8 });

/**
 * The five things off the pass, and what each one does to a tray.
 *
 * `mass` is torque per unit of offset and `foot` is how wide its base is, which
 * is what decides whether the next one can sit beside it. A napkin wad weighs
 * nothing and takes up room; a stack of small plates is the opposite. Between
 * them those two make a tray that is awkward in two different ways.
 */
export const ITEMS = Object.freeze([
  { id: 'cup', name: 'Cup', mass: 0.55, foot: 0.44, weight: 3 },
  { id: 'plate', name: 'Small plate', mass: 0.95, foot: 0.62, weight: 3 },
  { id: 'fork', name: 'Fork', mass: 0.2, foot: 0.22, weight: 2 },
  { id: 'spoon', name: 'Spoon', mass: 0.2, foot: 0.22, weight: 2 },
  { id: 'napkin', name: 'Napkin wad', mass: 0.08, foot: 0.7, weight: 2 },
]);

/** The knock a full-rim catch of one kilo puts into the tray, on landing. */
const CATCH = 0.2;

/**
 * Tilt per foot of tray movement, and how much faster movement multiplies it.
 *
 * A tray swings against the hand: go right and the load hangs left. The cost is
 * per foot travelled, so crossing the lane is never free, and it is multiplied
 * by how fast you crossed, so the same foot costs about twice as much at a
 * lunge as at a walk. That is the whole lesson of the class in two constants.
 */
const SWAY = 0.04;
const LUNGE = 4.5;

/**
 * How fast the tray itself will travel, in feet a second.
 *
 * The hand is the mouse and the tray is not: a tray has weight and it follows.
 * Without a ceiling here the tray teleports wherever the cursor went, which
 * both looks wrong and makes the sway term meaningless, because a teleport is
 * an infinite speed and an infinite speed tips any tray at any tier. Nine feet
 * a second crosses the lane in about a second, which is a brisk walk with a
 * loaded tray and is exactly what the class is asking you not to do.
 */
const TRAY_SPEED = 9;

/** What fraction of the wobble is steadied away each second. */
const STEADY = 0.9;

/**
 * How much off-centre moment a hand under a tray can hold, in kilo-rims.
 *
 * One kilo held out at the rim is one of these. Three means a small plate on
 * the edge is a third of the way to over and an armful of crockery all shoved
 * to one side is well past it, so the tray gets less forgiving exactly as the
 * round gets better, which is the only interesting shape this could have.
 */
const HOLD = 3;

/**
 * How much worse an off-centre thing is for every layer it is off the tray.
 *
 * A cup on top of three plates is further from the hand than the same cup on
 * the tray, so it pulls harder. Twelve per cent a layer, which is small enough
 * that building upward is still the right answer and large enough that
 * building upward *off to one side* is not.
 */
const LEAN = 0.12;

/**
 * How many things go on top of each other before the next one has to sit
 * beside them instead. Four high, counting the one on the tray.
 */
const STACK_MAX = 3;

export function createRefresh({ row, seed = 'refresh' }) {
  const rng = createRng(hashSeed(seed, row.level));

  let elapsed = 0;
  let nextAt = 0.6;
  let spawned = 0;
  let falling = [];
  /** What is on the tray: `{ item, offset }`, offset in feet from the middle. */
  let stack = [];
  let caught = 0;
  let dropped = 0;
  /**
   * The transient. A catch lands, the tray knocks; you move, it swings. Both
   * settle out over about a second because between them you are doing nothing
   * else but steadying it.
   *
   * Kept apart from the load's own balance below, because the two are different
   * things and reading them as one number was what made the first cut of this
   * unplayable: a tray that was perfectly loaded still tipped, purely from the
   * accumulated knocks of having loaded it.
   */
  let wobble = 0;
  let tipped = false;
  let trayX = 0;
  let wanted = 0;

  function spawn() {
    const item = rng.weighted(ITEMS);
    falling = [
      ...falling,
      {
        id: `i${spawned}`,
        item,
        x: (rng.next() - 0.5) * (LANE.width - 1.2),
        y: LANE.top,
      },
    ];
    spawned += 1;
  }

  /**
   * Where a thing landing at this offset comes to rest, and what it lands on.
   *
   * It stacks. A tray gets bussed by putting the next cup on top of the last
   * one, and it has to work that way here or the arithmetic stops making
   * sense: two and a half feet of tray holds about six things side by side and
   * the fifth level asks for twenty-five, so a tray that only spread sideways
   * had everything jammed against a rim by the eighth catch and went over for
   * reasons the player could do nothing about.
   *
   * So a thing sits on whatever it overlaps, one layer up, and only slides as
   * far as it must to stay fully on the tray. Which is the Tetris of it: where
   * you catch something decides where it can go, and a column built up one
   * side is a column that is still up that side four catches later.
   */
  function settle(offset, foot) {
    const rim = TRAY.width / 2;
    const hold = (x) => Math.max(-rim + foot / 2, Math.min(rim - foot / 2, x));
    const columnAt = (x) => {
      let layer = 0;
      for (const sitting of stack) {
        if (Math.abs(x - sitting.offset) < (sitting.item.foot + foot) / 2) {
          layer = Math.max(layer, sitting.layer + 1);
        }
      }
      return layer;
    };

    const wanted = hold(offset);
    const layer = columnAt(wanted);
    if (layer <= STACK_MAX) return { offset: wanted, layer };

    /*
     * The column is as high as a column goes, so it slides off to the side.
     *
     * Without this the tray is beaten by one trivial strategy: put every
     * catch dead centre and they build a single tower at nought, which is
     * perfectly balanced however many are on it, and a careless round that
     * happens to track each item exactly never tips at any level. Four things
     * high is where a stack of crockery stops being a stack, and past it the
     * next one has to find room beside — which is what puts weight off the
     * middle and is the whole game again.
     */
    let best = null;
    for (let step = 1; step <= 24 && !best; step += 1) {
      const reach = (step * foot) / 2;
      for (const side of [-1, 1]) {
        const at = hold(wanted + side * reach);
        if (Math.abs(at - wanted) < 1e-6) continue;
        const found = columnAt(at);
        if (found > STACK_MAX) continue;
        // Both sides are tried at every distance and the one nearer the middle
        // of the tray wins a tie, so nothing in here has a handedness. Trying
        // one side first and taking it made every full tray lean left.
        if (!best || found < best.layer || (found === best.layer && Math.abs(at) < Math.abs(best.offset))) {
          best = { offset: at, layer: found };
        }
      }
    }
    if (best) return best;
    // Nowhere left on the tray. It goes on top of the pile, and it will show.
    return { offset: wanted, layer: layer };
  }

  /**
   * Where the weight actually is, -1 at one rim and 1 at the other.
   *
   * This is the Tetris of it. Every catch goes somewhere on the tray and stays
   * there, so what you are managing is not a count, it is a centre of mass:
   * eleven forks on one side is a tray that goes over and four plates spread
   * properly is one that does not. A napkin wad takes up room and weighs
   * nothing, a small plate is the reverse, and that is the whole reason there
   * are five kinds of thing rather than one.
   *
   * The *net* moment, not the average offset. That distinction is the whole
   * difference between a tray you can play and one you cannot: normalised by
   * the load, a single plate caught on the rim reads as fully over and the
   * first three catches of every round are a coin toss. As a moment against
   * what a hand can hold, a light tray is forgiving and a loaded one is not,
   * which is both what a tray does and the shape this class wants — the round
   * gets harder as you succeed at it.
   */
  function balanceOf() {
    const rim = TRAY.width / 2;
    const moment = stack.reduce(
      (sum, entry) => sum + ((entry.item.mass * entry.offset) / rim) * (1 + entry.layer * LEAN),
      0
    );
    return moment / HOLD;
  }

  /** What the spirit level reads, and what tips the tray. */
  function tiltOf() {
    return Math.max(-2, Math.min(2, balanceOf() + wobble));
  }

  return {
    get level() {
      return row.level;
    },
    get target() {
      return row.target;
    },
    /** A tipped tray is a lost round, and the brief is blunt about it. */
    get score() {
      return tipped ? 0 : caught;
    },
    get caught() {
      return caught;
    },
    get dropped() {
      return dropped;
    },
    get balance() {
      return balanceOf();
    },

    get wobble() {
      return wobble;
    },

    get tilt() {
      return tiltOf();
    },
    get tipped() {
      return tipped;
    },
    get trayX() {
      return trayX;
    },
    get falling() {
      return falling.map((entry) => ({ ...entry }));
    },
    get stack() {
      return stack.map((entry) => ({ ...entry }));
    },
    get done() {
      return tipped || (spawned >= row.items && falling.length === 0);
    },
    get passed() {
      return !tipped && caught >= row.target;
    },

    /** Where the tray is being asked to be, in lane feet. Clamped to the lane. */
    moveTo(x) {
      const rim = LANE.width / 2 - TRAY.width / 2;
      wanted = Math.max(-rim, Math.min(rim, x));
    },

    step(dt) {
      if (tipped) return;
      elapsed += dt;

      // The tray follows the hand, and how far and how fast it had to go is
      // the sway. Taken before the catches, so a thing caught mid-lunge is
      // landing on a tray that is already moving.
      const was = trayX;
      const asked = wanted - was;
      const reach = TRAY_SPEED * dt;
      trayX = was + Math.max(-reach, Math.min(reach, asked));
      const travelled = trayX - was;
      const speed = Math.abs(travelled) / Math.max(dt, 1e-4);
      wobble -= travelled * SWAY * (1 + speed / LUNGE);

      if (spawned < row.items && elapsed >= nextAt) {
        spawn();
        nextAt = elapsed + 1 / row.rate;
      }

      const rim = TRAY.width / 2;
      const still = [];
      for (const entry of falling) {
        entry.y -= row.fall * dt;
        if (entry.y > TRAY.y) {
          still.push(entry);
          continue;
        }
        const offset = entry.x - trayX;
        if (Math.abs(offset) > rim) {
          dropped += 1;
          continue;
        }
        const seat = settle(offset, entry.item.foot);
        stack = [...stack, { item: entry.item, offset: seat.offset, layer: seat.layer }];
        caught += 1;
        wobble += (seat.offset / rim) * entry.item.mass * CATCH;
      }
      falling = still;

      wobble -= wobble * Math.min(1, STEADY * dt);
      wobble = Math.max(-2, Math.min(2, wobble));
      if (Math.abs(tiltOf()) >= row.tipAt) tipped = true;
    },
  };
}
